import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { CORE_PRICE_CENTS, ADD_ONS, formatPrice } from "@/lib/personalization";

const PricingSection = () => {
  const navigate = useNavigate();

  return (
    <section id="pricing" className="py-24 bg-secondary/50">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-sm font-semibold uppercase tracking-widest text-champagne mb-3">
            Transparent Pricing
          </p>
          <h2 className="font-display text-4xl md:text-5xl text-foreground mb-4">
            Simple, Transparent Pricing for Personalized Care
          </h2>
          <p className="text-muted-foreground">
            Start with a physician-guided foundation and expand your plan with therapies tailored to your goals.
          </p>
          <p className="text-sm text-muted-foreground mt-4 italic">
            Most patients pay between $349–$599/month depending on their personalized plan.
          </p>
          <p className="text-xs text-muted-foreground/80 mt-2 max-w-md mx-auto leading-relaxed">
            Membership covers physician consultations and care coordination. Laboratory tests and prescription medications are billed separately when applicable.
          </p>
          <p className="text-xs text-champagne font-medium mt-3">
            Limited new patient capacity each month — applications reviewed in order received.
          </p>
        </div>

        <div className="max-w-lg mx-auto">
          <Card className="border-primary shadow-lg bg-card">
            <CardContent className="p-8 space-y-6">
              <div>
                <h3 className="font-display text-xl text-foreground">
                  Core Membership
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Full clinical access: clinician evaluation, personalized
                  treatment plans, prescription management & ongoing provider oversight.
                </p>
              </div>

              <div className="flex items-baseline gap-1">
                <span className="font-display text-4xl text-foreground">
                  {formatPrice(CORE_PRICE_CENTS)}
                </span>
                <span className="text-muted-foreground text-sm">/month</span>
              </div>

              <p className="text-xs text-champagne font-semibold">
                Intro Offer: $249–$299 first month
              </p>

              <Button
                className="w-full"
                size="lg"
                onClick={() => navigate("/get-started")}
              >
                Get Started
              </Button>

              <ul className="space-y-3">
                {[
                  "Clinician evaluation",
                  "Personalized treatment plan",
                  "Ongoing provider oversight",
                  "Prescription management (if appropriate)",
                  "Access to additional therapies",
                  "Unlimited clinician messaging",
                ].map((f) => (
                  <li
                    key={f}
                    className="flex items-start gap-2 text-sm text-muted-foreground"
                  >
                    <Check
                      size={16}
                      className="text-primary mt-0.5 shrink-0"
                    />
                    {f}
                  </li>
                ))}
              </ul>

              <div className="border-t border-border pt-4">
                <p className="text-xs font-semibold text-foreground mb-2">
                  Optional add-ons
                </p>
                <div className="space-y-1.5">
                  {ADD_ONS.map((a) => (
                    <p
                      key={a.id}
                      className="text-xs text-muted-foreground flex justify-between"
                    >
                      <span>{a.name}</span>
                      <span>+{formatPrice(a.priceCents)}/mo</span>
                    </p>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
